import {
  browserPopupRedirectResolver,
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  type User,
} from 'firebase/auth';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { COL } from '../../shared/constants';
import type { FamilyConfig } from '../../shared/types';
import { auth, db, googleProvider } from './firebase';
import { closeMediaSession, openMediaSession } from './session';

export type Membership =
  | { state: 'loading' }
  | { state: 'signed-out' }
  | { state: 'not-a-member'; email: string }
  | { state: 'member'; user: User; isAdmin: boolean };

/**
 * Anyone can sign in with Google; almost nobody is in this family. The client
 * check here is only so we can show the right screen — the real enforcement is
 * in the Firestore rules, the createUpload callable and the gateway Worker.
 *
 * No data query fires until this resolves to 'member'.
 */
export function watchMembership(onChange: (m: Membership) => void): () => void {
  onChange({ state: 'loading' });

  return onAuthStateChanged(auth, async (user) => {
    if (!user) {
      onChange({ state: 'signed-out' });
      return;
    }

    const email = (user.email ?? '').toLowerCase();
    if (!email || !user.emailVerified) {
      onChange({ state: 'not-a-member', email });
      return;
    }

    /*
     * Straight in, for someone this phone has already seen let in, whatever
     * the signal: the chat draws from the phone's own copy while the checks
     * below run behind it.
     *
     * It used to go straight in only with a photo session still good or no
     * signal at all, so a weak one was the worst case of the three -- the
     * family list read from the server first, which Firestore can take ten
     * seconds to give up on, then the session, with the loading screen up
     * throughout. Photos no longer need the session to be waited for: the
     * ones on the phone show without it, and the rest are asked for again
     * when it opens (./mediaRetry).
     */
    const known = readKnown(user.uid);
    const early = known !== null;
    if (early) onChange({ state: 'member', user, isAdmin: known.isAdmin });

    let family: FamilyConfig | undefined;
    try {
      const snap = await getDoc(doc(db, COL.configFamily));
      family = snap.data() as FamilyConfig | undefined;
    } catch (err) {
      // Let in already and only offline now: still in. Only a refusal says
      // otherwise -- rules deny the read to non-members, and that is the answer.
      if (early && (err as { code?: string }).code !== 'permission-denied') return;
      forgetKnown();
      onChange({ state: 'not-a-member', email });
      return;
    }

    const members = (family?.members ?? []).map((m) => m.toLowerCase());
    if (!members.includes(email)) {
      forgetKnown();
      onChange({ state: 'not-a-member', email });
      return;
    }

    // Never waited for: it only matters the first time anyone signs in, and
    // offline its write does not finish until the server has seen it.
    void ensureUserDoc(user, email).catch((err) => console.warn('could not write the user doc', err));

    // Not allowed to block reaching the member state either. The media
    // gateway lives on its own hostname, and if that is not reachable the app
    // should still sign you in with broken images rather than spinning on a
    // loading screen forever. A 403 means the edge allowlist has not caught
    // up yet; Firestore has already said this person is a member.
    const media = openMediaSession(user).catch((err) =>
      console.warn('media session unavailable, photos will not load', err),
    );
    if (!early) await media;

    const admins = (family?.admins ?? []).map((m) => m.toLowerCase());
    const isAdmin = admins.includes(email);
    rememberKnown(user.uid, isAdmin);
    // Already in: said again only if being an admin has changed meanwhile.
    if (!early || known.isAdmin !== isAdmin) onChange({ state: 'member', user, isAdmin });
  });
}

/*
 * Who this phone has seen let in, and whether as an admin. A hint for opening
 * quickly, never a permission: the rules, createUpload and the gateway decide,
 * and the check above still runs every time and takes it back if it is wrong.
 */
const KNOWN_KEY = 'givisnap:knownMember';

function readKnown(uid: string): { isAdmin: boolean } | null {
  try {
    const [who, admin] = (localStorage.getItem(KNOWN_KEY) ?? '').split(' ');
    return who === uid ? { isAdmin: admin === '1' } : null;
  } catch {
    return null;
  }
}

function rememberKnown(uid: string, isAdmin: boolean): void {
  try {
    localStorage.setItem(KNOWN_KEY, `${uid} ${isAdmin ? 1 : 0}`);
  } catch {
    // Blocked storage: every opening checks first, as before.
  }
}

function forgetKnown(): void {
  try {
    localStorage.removeItem(KNOWN_KEY);
  } catch {
    // Nothing kept to forget.
  }
}

async function ensureUserDoc(user: User, email: string): Promise<void> {
  const ref = doc(db, COL.users, user.uid);
  const snap = await getDoc(ref);
  if (snap.exists()) return;
  await setDoc(ref, {
    // First name only, which is how everyone in a family of eleven is
    // referred to anyway, and what every screen showing a name expects.
    displayName: (user.displayName ?? email.split('@')[0]).trim().split(/\s+/)[0],
    photoURL: user.photoURL ?? null,
    email,
    createdAt: serverTimestamp(),
  });
}

export function signIn(): Promise<unknown> {
  // The popup's helpers load here, at sign-in, rather than on every opening.
  return signInWithPopup(auth, googleProvider, browserPopupRedirectResolver);
}

export async function leave(): Promise<void> {
  forgetKnown();
  await closeMediaSession();
  await signOut(auth);
}
